import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Heart, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WishlistButton = ({ 
  itemId, 
  itemType = 'product', 
  size = 'icon',
  variant = 'ghost',
  className = '',
  showText = false 
}) => {
  const { isAuthenticated } = useAuth();
  const [inWishlist, setInWishlist] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (isAuthenticated && itemId) {
      checkWishlistStatus();
    } else {
      setChecking(false);
    }
  }, [isAuthenticated, itemId, itemType]);

  const checkWishlistStatus = async () => {
    try {
      const response = await axios.get(`${API}/wishlist/check/${itemType}/${itemId}`);
      setInWishlist(response.data.in_wishlist);
    } catch (error) {
      console.error('Failed to check wishlist status:', error);
    } finally {
      setChecking(false);
    }
  };

  const toggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      toast.error('Please sign in to save items');
      return;
    }

    setLoading(true);
    try {
      if (inWishlist) {
        await axios.post(`${API}/wishlist/remove`, {
          item_type: itemType,
          item_id: itemId
        });
        setInWishlist(false);
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist/add`, {
          item_type: itemType,
          item_id: itemId
        });
        setInWishlist(true);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error('Failed to update wishlist');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <Button 
        variant={variant} 
        size={size} 
        className={className}
        disabled
      >
        <Loader2 className="h-4 w-4 animate-spin" />
      </Button>
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      className={`${className} ${inWishlist ? 'text-red-500 hover:text-red-600' : 'text-muted-foreground hover:text-red-500'}`}
      onClick={toggleWishlist}
      disabled={loading}
      data-testid={`wishlist-btn-${itemType}-${itemId}`}
      title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <>
          <Heart className={`h-4 w-4 ${inWishlist ? 'fill-current' : ''}`} />
          {showText && (
            <span className="ml-2">{inWishlist ? 'Saved' : 'Save'}</span>
          )}
        </>
      )}
    </Button>
  );
};
